import { callsOptions, messagesOptions, useCreateMessageMutation } from '@cartwheel/api/v1'
import { Avatar, Button, LoadingView, useToast } from '@cartwheel/components'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import BrowserCallModal from './browser-call-modal'

// ─── Types ────────────────────────────────────────────────────────────────────

interface SharedProfile {
  name: string
  relationship: string
  url: string
}

interface Recipient {
  name: string | null
  role: string
  phone: string | null
  kind: 'self' | 'primary_guardian' | 'guardian' | 'school_contact'
  sharedProfiles: SharedProfile[]
}

interface ClientCommunicationsProps {
  clientId: string
  recipients: Recipient[]
}

// ─── Root ─────────────────────────────────────────────────────────────────────

/**
 * Communications tab for the student profile page.
 *
 * Renders one RecipientCard per recipient (student and/or guardians). Each card
 * has an independent Call button (BrowserCallModal) and a collapsible SMS thread
 * filtered to that recipient's phone number via /v1/messages?external_number=.
 */
export default function ClientCommunications({ clientId, recipients }: ClientCommunicationsProps) {
  if (recipients.length === 0) {
    return (
      <p className="text-sm text-charcoal-50 py-12 text-center">
        No contacts found for this student.
      </p>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-ginto-nord-md text-charcoal-75 mb-4">Contacts</h2>
        <div className="space-y-4">
          {recipients.map((r, i) => (
            <RecipientCard key={r.phone ?? `recipient-${i}`} clientId={clientId} recipient={r} />
          ))}
        </div>
      </div>
      <CallHistory clientId={clientId} recipients={recipients} />
    </div>
  )
}

// ─── Recipient card ───────────────────────────────────────────────────────────

function RecipientCard({ clientId, recipient }: { clientId: string; recipient: Recipient }) {
  const [browserCallOpen, setBrowserCallOpen] = useState(false)
  const [smsOpen, setSmsOpen] = useState(false)
  const hasPhone = !!recipient.phone
  const queryClient = useQueryClient()

  const displayName = recipient.name ?? 'Unknown'
  const label = `${displayName} — ${recipient.role}`
  const sharedProfiles = recipient.sharedProfiles ?? []

  const handleBrowserCallClose = () => {
    setBrowserCallOpen(false)
    void queryClient.invalidateQueries({ queryKey: [{ _id: 'calls' }] })
  }

  return (
    <div className="rounded-lg border border-gray-stroke bg-white">
      {/* Header row */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-stroke">
        <div>
          <p className="text-sm font-semibold text-charcoal">{displayName}</p>
          <p className="text-xs text-charcoal-50">{recipient.role}</p>
          {!hasPhone && <p className="text-xs text-brick mt-0.5">No phone number on file</p>}
          {sharedProfiles.length > 0 && (
            <div className="mt-1.5">
              <span className="inline-flex items-center rounded-md bg-gold-25 px-2 py-0.5 text-xs font-medium text-bronze">
                Shared number
              </span>
              <p className="text-xs text-charcoal-50 mt-1">
                Also used by{' '}
                {sharedProfiles.map((profile, index) => (
                  <span key={profile.url}>
                    {index > 0 && (index === sharedProfiles.length - 1 ? ', and ' : ', ')}
                    <a href={profile.url} className="text-indigo underline hover:text-indigo-75">
                      {profile.name}
                    </a>
                    {profile.relationship !== 'their own number' && (
                      <span> ({profile.relationship})</span>
                    )}
                  </span>
                ))}
              </p>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => setBrowserCallOpen(true)}
            disabled={!hasPhone}
            title={hasPhone ? `Call ${displayName} in browser` : 'No phone number on file'}
          >
            <i className="fa-solid fa-globe mr-1.5" aria-hidden="true" />
            Portal Phone Call
          </Button>
          <Button
            variant="secondary"
            onClick={() => setSmsOpen((v) => !v)}
            disabled={!hasPhone}
            title={hasPhone ? `Portal Chat with ${displayName}` : 'No phone number on file'}
          >
            <i className="fa-solid fa-message mr-1.5" aria-hidden="true" />
            {smsOpen ? 'Hide Portal Chat' : 'Portal Chat'}
          </Button>
        </div>
      </div>

      {/* Collapsible SMS thread */}
      {smsOpen && recipient.phone && (
        <SmsThread clientId={clientId} externalNumber={recipient.phone} recipientLabel={label} />
      )}

      <BrowserCallModal
        isOpen={browserCallOpen}
        onClose={handleBrowserCallClose}
        targetNumber={recipient.phone ?? undefined}
        targetLabel={label}
        clientId={clientId}
      />
    </div>
  )
}

// ─── SMS thread ───────────────────────────────────────────────────────────────

function SmsThread({
  clientId,
  externalNumber,
  recipientLabel,
}: {
  clientId: string
  externalNumber: string
  recipientLabel: string
}) {
  const toast = useToast()
  const [body, setBody] = useState('')
  const messagesRef = useRef<HTMLDivElement | null>(null)

  const messagesQuery = useQuery({
    ...messagesOptions({ query: { client_id: clientId, external_number: externalNumber } }),
    refetchInterval: 10_000,
  })

  const sendMutation = useCreateMessageMutation({
    onSuccess: () => {
      setBody('')
      void messagesQuery.refetch()
    },
    onError: () => {
      toast.error({ description: 'Could not send the message. Please try again.' })
    },
  })

  type MessageItem = {
    id: string
    direction: string
    body: string
    sender?: { name?: string | null; avatar_url?: string | null } | null
    sent_at?: string | null
    status: string
    created_at: string
  }

  const messages = (messagesQuery.data?.items ?? []) as MessageItem[]

  // biome-ignore lint/correctness/useExhaustiveDependencies: messages.length triggers scroll when new messages arrive
  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight
    }
  }, [messages.length])

  const handleSend = () => {
    if (!body.trim()) return
    sendMutation.mutate({
      body: { client_id: clientId, body: body.trim(), phone_number: externalNumber },
    })
  }

  if (messagesQuery.isPending) return <LoadingView title="Loading messages…" />

  return (
    <div>
      <div className="px-4 py-2 bg-sand-10 border-b border-gray-stroke">
        <p className="text-xs text-charcoal-50 font-medium">
          Portal Chat with {recipientLabel} &middot; {externalNumber}
        </p>
      </div>

      <div ref={messagesRef} className="px-4 py-3 space-y-2 max-h-64 overflow-y-auto bg-sand-10">
        {messages.length === 0 ? (
          <p className="text-sm text-charcoal-50 text-center py-4">No messages yet.</p>
        ) : (
          messages.map((m) => (
            <MessageBubble
              key={m.id}
              direction={m.direction}
              body={m.body}
              status={m.status}
              senderName={m.sender?.name}
              senderAvatarUrl={m.sender?.avatar_url}
              timestamp={m.sent_at || m.created_at}
            />
          ))
        )}
      </div>

      <div className="border-t border-gray-stroke px-4 py-3 bg-white">
        <textarea
          className="w-full rounded-md border border-gray-stroke p-3 text-sm focus:outline-none focus:ring-2 focus:ring-mint focus:border-mint resize-none"
          rows={3}
          placeholder="Type your message…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <p className="mt-2 text-xs text-charcoal-50">Your signature is appended automatically</p>
        <div className="flex justify-end mt-2">
          <Button onClick={handleSend} disabled={!body.trim() || sendMutation.isPending}>
            {sendMutation.isPending ? 'Sending…' : 'Send'}
          </Button>
        </div>
      </div>
    </div>
  )
}

// ─── Call history ─────────────────────────────────────────────────────────────

type CallItem = {
  id: string
  status: string
  terminal: boolean
  target_number: string
  target_label?: string | null
  call_type?: string | null
  duration_seconds?: number | null
  ended_reason?: string | null
  error_message?: string | null
  initiated_by?: { name?: string | null } | null
  created_at: string
}

const STATUS_COLORS: Record<string, string> = {
  completed: 'bg-mint-25 text-forest',
  in_progress: 'bg-sky-50 text-indigo',
  busy: 'bg-gold-25 text-bronze',
  no_answer: 'bg-gold-25 text-bronze',
  failed: 'bg-brick-10 text-brick',
  canceled: 'bg-sand-25 text-charcoal-50',
}

function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null || seconds === 0) return ''
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return m > 0 ? `${m}m ${s}s` : `${s}s`
}

type CallHistoryTab = 'all' | 'missed' | 'outbound'

function CallHistory({ clientId, recipients }: { clientId: string; recipients: Recipient[] }) {
  const [tab, setTab] = useState<CallHistoryTab>('all')

  const callsQuery = useQuery(
    callsOptions({
      query: {
        client_id: clientId,
        call_type: tab === 'missed' ? 'inbound' : undefined,
        direction: tab === 'outbound' ? 'outbound' : undefined,
        limit: 25,
      },
    })
  )

  // Build a phone → label lookup from our recipients so we can annotate calls.
  const phoneLabels = useMemo(() => {
    const map: Record<string, string> = {}
    for (const r of recipients) {
      if (r.phone) map[r.phone.replace(/\D/g, '')] = `${r.name ?? 'Unknown'} (${r.role})`
    }
    return map
  }, [recipients])

  const calls = (callsQuery.data?.items ?? []) as CallItem[]

  const resolveLabel = (call: CallItem) => {
    if (call.target_label) return call.target_label
    const digits = call.target_number.replace(/\D/g, '')
    return phoneLabels[digits] ?? call.target_number
  }

  const emptyMessage = {
    all: 'No calls yet.',
    missed: 'No missed calls.',
    outbound: 'No outbound calls yet.',
  }[tab]

  return (
    <div>
      <h2 className="text-lg font-ginto-nord-md text-charcoal-75 mb-3">Call history</h2>

      <div className="flex gap-1 border-b border-gray-stroke mb-3">
        <CallHistoryTabButton active={tab === 'all'} onClick={() => setTab('all')}>
          <i className="fa-solid fa-list mr-1.5" aria-hidden="true" />
          All
        </CallHistoryTabButton>
        <CallHistoryTabButton active={tab === 'missed'} onClick={() => setTab('missed')}>
          <i className="fa-solid fa-phone-missed mr-1.5" aria-hidden="true" />
          Missed
        </CallHistoryTabButton>
        <CallHistoryTabButton active={tab === 'outbound'} onClick={() => setTab('outbound')}>
          <i className="fa-solid fa-phone-arrow-up-right mr-1.5" aria-hidden="true" />
          Outbound
        </CallHistoryTabButton>
      </div>

      {callsQuery.isPending ? (
        <LoadingView title="Loading calls…" />
      ) : calls.length === 0 ? (
        <p className="text-sm text-charcoal-50 py-8 text-center border border-gray-stroke rounded-lg">
          {emptyMessage}
        </p>
      ) : (
        <ul className="divide-y divide-gray-stroke border border-gray-stroke rounded-lg overflow-hidden">
          {calls.map((c) => {
            const colorClass = STATUS_COLORS[c.status] ?? 'bg-sand-25 text-charcoal-75'
            const duration = formatDuration(c.duration_seconds)
            const when = new Date(c.created_at).toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            })

            const isBrowser = c.call_type === 'browser'

            return (
              <li key={c.id} className="px-4 py-3 bg-white flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-charcoal truncate">{resolveLabel(c)}</p>
                  <p className="text-xs text-charcoal-50 mt-0.5">
                    {when}
                    {duration ? ` · ${duration}` : ''}
                    {c.initiated_by?.name ? ` · by ${c.initiated_by.name}` : ''}
                  </p>
                  {c.error_message && (
                    <p className="text-xs text-brick mt-0.5">{c.error_message}</p>
                  )}
                </div>
                <div className="shrink-0 flex items-center gap-1.5">
                  <span className="text-xs font-medium px-2 py-1 rounded-md bg-sand-10 text-charcoal-50 border border-gray-stroke">
                    {c.call_type === 'inbound' ? (
                      <>
                        <i className="fa-solid fa-phone-missed mr-1" aria-hidden="true" />
                        Missed
                      </>
                    ) : isBrowser ? (
                      <>
                        <i className="fa-solid fa-globe mr-1" aria-hidden="true" />
                        Browser
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-phone mr-1" aria-hidden="true" />
                        Bridge
                      </>
                    )}
                  </span>
                  <span className={`text-xs font-medium px-2 py-1 rounded-md ${colorClass}`}>
                    {c.ended_reason ?? c.status}
                  </span>
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

function CallHistoryTabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'px-3 py-1.5 text-xs font-medium border-b-2 -mb-px transition-colors',
        active
          ? 'border-forest text-forest'
          : 'border-transparent text-charcoal-50 hover:text-charcoal',
      ].join(' ')}
    >
      {children}
    </button>
  )
}

// ─── Message bubble ───────────────────────────────────────────────────────────

function MessageBubble({
  direction,
  body,
  status,
  senderName,
  senderAvatarUrl,
  timestamp,
}: {
  direction: string
  body: string
  status: string
  senderName: string | null | undefined
  senderAvatarUrl: string | null | undefined
  timestamp: string
}) {
  const outbound = direction === 'outbound'
  const formatted = new Date(timestamp).toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })

  const avatar = outbound ? (
    <div className="flex-shrink-0 ml-2 self-end">
      <Avatar
        src={senderAvatarUrl ?? ''}
        alt={senderName ?? ''}
        sizeClassName="w-7 h-7"
        className="border border-white/30"
      />
    </div>
  ) : null

  return (
    <div className={`flex items-end ${outbound ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap ${
          outbound ? 'bg-forest text-white' : 'bg-white border border-gray-stroke text-charcoal'
        }`}
      >
        <p>{body}</p>
        <p className={`text-xs mt-1 ${outbound ? 'text-white/80' : 'text-charcoal-50'}`}>
          {senderName ? `${senderName} • ` : ''}
          {formatted}
          {outbound && status ? ` • ${status}` : ''}
        </p>
      </div>
      {avatar}
    </div>
  )
}
